import { NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { CourseManagementService } from '@edtech-ui/core/services/course-management/course-management.service';
import { alphanumericValidator } from '@edtech-ui/core/utils/create-course-validators';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-admin-course-creation-form',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatIconModule, NgIf],
  templateUrl: './admin-course-creation-form.component.html',
  styleUrl: './admin-course-creation-form.component.scss'
})
export class AdminCourseCreationFormComponent implements OnInit {

  createCourseForm!: FormGroup;
  classroomId = '';
  isDragging = false;
  selectedFile: File | string | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private courseService: CourseManagementService,
    private toast: ToastrService
  ) {
    const payload = this.courseService.getPayload();

    this.createCourseForm = new FormGroup({
      course_title: new FormControl(payload.course_title, [
        Validators.required,
        Validators.maxLength(80),
        Validators.pattern(/^[A-Za-z0-9 .,;&'"()-_]+$/)
      ]),

      course_code: new FormControl(payload.course_code, [
        Validators.required,
        Validators.pattern(/^[A-Za-z0-9_-]+$/)
      ]),

      course_description: new FormControl(payload.course_description, [
        Validators.required,
        Validators.maxLength(280),
        Validators.pattern(/^[A-Za-z0-9 .,;&'"()-_]+$/)
      ]),

      grade_level: new FormControl(payload.grade_level, [
        Validators.required
      ]),

      rating: new FormControl(payload.rating ?? null, [
        Validators.min(1),
        Validators.max(5)
      ]),

      course_thumbnail: new FormControl(payload.course_thumbnail ?? null)
    });


    this.createCourseForm.valueChanges.subscribe(val => {
      this.courseService.updatePayload({
        course_code: val.course_code,
        course_title: val.course_title,
        course_description: val.course_description,
        grade_level: val.grade_level,

        credits: val.credits ? Number(val.credits) : 0,
        internal_marks: val.cie_marks ? Number(val.cie_marks) : 0,
        exam_marks: val.see_marks ? Number(val.see_marks) : 0,

        course_thumbnail: val.course_thumbnail ?? null,

        rating:
          val.rating !== null && val.rating !== ''
            ? Number(val.rating)
            : undefined
      });
    });
  }

  ngOnInit(): void {
    const payload = this.courseService.getPayload();

    if (payload.course_thumbnail) {
      this.selectedFile = payload.course_thumbnail;
    }
  }

  goToNextPage(): void {
    const formValues = this.createCourseForm.value;

    const credits = formValues.credits ? Number(formValues.credits) : null;
    const cieMarks = formValues.cie_marks ? Number(formValues.cie_marks) : null;
    const seeMarks = formValues.see_marks ? Number(formValues.see_marks) : null;

    if (credits === 0 || cieMarks === 0 || seeMarks === 0) {
      this.toast.error('Credits, CIE Marks, and SEE Marks cannot be 0.');
      return;
    }

    if (
      this.createCourseForm.get('course_title')?.invalid ||
      this.createCourseForm.get('course_code')?.invalid ||
      this.createCourseForm.get('course_description')?.invalid ||
      this.createCourseForm.get('grade_level')?.invalid
    ) {
      this.toast.error('Please fill all required fields correctly.');
      return;
    }

    this.router.navigate(['/admin-student-platform/course-setup/objectives']);
  }

  onFileSelect(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) this.handleFile(file);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(): void {
    this.isDragging = false;
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.handleFile(file);
  }

  private handleFile(file: File): void {
    const validTypes = ['image/png', 'image/jpeg'];
    const maxSize = 5 * 1024 * 1024;

    if (!validTypes.includes(file.type)) {
      this.toast.error('Only PNG or JPG images are allowed');
      return;
    }

    if (file.size > maxSize) {
      this.toast.error('File size must be less than 5MB');
      return;
    }

    this.selectedFile = file;
    this.createCourseForm.patchValue({ course_thumbnail: file });

    this.courseService.updatePayload({
      course_thumbnail: file
    });
  }

  getSelectedFileName(): string {
    if (!this.selectedFile) return '';

    if (this.selectedFile instanceof File) {
      return this.selectedFile.name;
    }
    return this.selectedFile.split('/').pop() ?? 'Uploaded image';
  }

}
